import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import FiltresPanel from './FiltresPanel';
import './Stock.css'; // `st-seg` / `st-tools` du sélecteur d'unité (cf. SelecteurUnite)

// Palette société alignée sur le reste de BI.
export const COULEURS = ['#2E5BFF', '#C9A84C', '#0F766E', '#9D174D', '#4D7C0F', '#B45309', '#475569'];
// « 12 mois glissants » retiré le 2026-10-12 (demande du user : sans intérêt
// pour le pilotage). Le serveur l'accepte encore ; un ancien lien qui le porte
// retombe sur le cumul année (cf. periodeUrl).
export const PERIODES = [
  { code: 'ytd', label: 'Cumul année (YTD)' },
];
export const periodeUrl = (params) => {
  const p = params.get('periode');
  return p && PERIODES.some((x) => x.code === p) ? p : 'ytd';
};

// ─── Formats ──────────────────────────────────────────────────────────────────
export const nb = (v) => new Intl.NumberFormat('fr-FR').format(Math.round(v || 0));
export const eur = (v) => nb(v) + ' €';
export const eurCourt = (v) => {
  const a = Math.abs(v || 0);
  if (a >= 1e6) return (v / 1e6).toLocaleString('fr-FR', { maximumFractionDigits: 1 }) + ' M€';
  if (a >= 1e3) return Math.round(v / 1e3).toLocaleString('fr-FR') + ' k€';
  return Math.round(v || 0) + ' €';
};
// Unité d'affichage des montants dans les tableaux croisés. « k€ » abrège (et
// bascule en M€ au-delà du million), « € » donne le montant exact — c'est ce
// qu'il faut pour rapprocher une cellule d'une facture, ce que l'arrondi au
// millier rend impossible (2 quand la facture dit 1 512 €).
// Sélecteur montré UNIQUEMENT quand la mesure courante est monétaire : un
// bouton sans effet sur « Volume » ou « Taux de marge » est pire que pas de
// bouton du tout.
export const UNITES = [{ code: 'k', label: 'k€' }, { code: 'e', label: '€' }];
export const montant = (v, unite) => (unite === 'e' ? eur(v) : eurCourt(v));
// Posé dans l'EN-TÊTE DE LA CARTE, à droite du titre — même disposition que le
// sélecteur d'unité d'OR en cours (`st-tools` + `st-seg` dans `cr-card-h`) :
// c'est un réglage d'affichage du tableau, pas un critère d'analyse comme
// Mesure et Dimension. Le mettre dans la barre du haut le rangeait avec des
// boutons qui changent CE QU'ON REGARDE, alors qu'il change COMMENT on le lit.
//
// `st-seg` appartient à Stock.css : la classe est disponible partout parce que
// CRA bundle toutes les feuilles en un seul fichier, mais on n'en dépend pas
// par accident — l'import ci-dessus la rend explicite.
export const SelecteurUnite = ({ unite, setUnite }) => (
  <>
    <span className="hint">montants en</span>
    <span className="st-seg">
      {UNITES.map((u) => (
        <button key={u.code} type="button" className={unite === u.code ? 'on' : ''}
          onClick={() => setUnite(u.code)}
          title={u.code === 'e' ? 'Montants exacts' : 'Montants abrégés (k€ / M€)'}>{u.label}</button>
      ))}
    </span>
  </>
);

export const pct1 = (v) => (v == null ? '—' : v.toLocaleString('fr-FR', { maximumFractionDigits: 1 }) + ' %');
export const moisCourt = (ym) => { const [a, m] = ym.split('-'); return new Date(a, m - 1, 1).toLocaleDateString('fr-FR', { month: 'short' }); };
export const moisAn = (ym) => { const [a, m] = ym.split('-'); return new Date(a, m - 1, 1).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }); };
export const signe = (v) => (v > 0 ? '+' : '') + nb(v);
export const signePct = (v) => (v == null ? '—' : (v > 0 ? '+' : '') + pct1(v));
// Cellule « vs N-1 » d'un tableau, colorée par signe. Deux formes :
//   • { valeur, n1, fmt } : écart en VALEUR ABSOLUE (valeur − n1), formaté par
//     fmt (nb, eurCourt, eur, kEur…) ; « — » sans historique N-1 ;
//   • { pt, suffixe } : écart d'un taux, en points (' pts') ou dans l'unité du
//     taux (' €' pour un taux horaire).
export const CelluleDelta = ({ valeur, n1, fmt = nb, pt, suffixe = ' pts' }) => {
  let val, txt;
  if (pt !== undefined) { val = pt; txt = val == null ? null : `${val > 0 ? '+' : ''}${pct1(val).replace(' %', suffixe)}`; }
  else { val = n1 ? valeur - n1 : null; txt = val == null ? null : (val > 0 ? '+' : '') + fmt(val); }
  if (txt == null) return <td className="vt-d">—</td>;
  return <td className={`vt-d ${val >= 0 ? 'pos' : 'neg'}`}>{txt}</td>;
};

// Les 12 mois de l'année en cours (janvier → décembre, libellé sans l'année)
// pour le sélecteur « Mois ». Les mois à venir sont listés mais désactivés.
// L'année précédente reste couverte par les comparaisons N-1 de chaque rapport.
export function moisAnnee(annee = new Date().getFullYear()) {
  const now = new Date(); const courant = now.getFullYear() * 12 + now.getMonth();
  return Array.from({ length: 12 }, (_, i) => {
    const code = `${annee}-${String(i + 1).padStart(2, '0')}`;
    const label = new Date(annee, i, 1).toLocaleDateString('fr-FR', { month: 'long' });
    return { code, label: label.charAt(0).toUpperCase() + label.slice(1), desactive: annee * 12 + i > courant };
  });
}
export const MOIS_DISPO = moisAnnee();

// ─── Le geste de filtrage, écrit une fois ───────────────────────────────────
// Toute ligne de répartition cliquable passe par ici (CLAUDE.md §4 bis) : un
// clic isole la valeur, recliquer la même annule.
//
// ⚠ `courant` est la valeur BRUTE de l'URL, jamais la liste résolue. Les listes
// résolues valent « toutes les sociétés » quand aucun filtre n'est posé : sur
// un périmètre à une seule société — le cas de la plupart des habilitations —
// tester `selected.length === 1 && selected[0] === code` rendait la ligne
// perpétuellement active et le reclic n'annulait rien.
export const basculer = (courant, code) => (courant === code ? '' : code);

// ─── Canal PR : comptoir / atelier ───────────────────────────────────────────
// Ce sont DEUX SERVICES, avec deux responsables : choisir l'un doit recaler
// toute la page, pas seulement un bloc. C'est donc un filtre du panneau, porté
// par l'URL (partageable, réversible), et non un réglage d'en-tête de carte.
// Trois états exclusifs — des cases à cocher auraient permis de tout décocher.
export const CHOIX_CANAL = [
  { code: '', nom: 'Comptoir + atelier' },
  { code: 'Comptoir', nom: 'Comptoir seul' },
  { code: 'Atelier', nom: 'Atelier seul' },
];
export function useCanal(f) {
  const canal = f.params.get('canal') || '';
  return {
    canal,
    choix: { titre: 'Canal', valeurs: CHOIX_CANAL, valeur: canal, onChoisir: (c) => f.setFiltre({ canal: c }) },
  };
}

// ─── Hook filtres (période/mois + sociétés) via URL — partagé par les 3 rapports ──
export function useFiltresVentes(societesDispo) {
  const [params, setParams] = useSearchParams();
  const periode = periodeUrl(params);
  const moisStr = params.get('mois') || '';
  const societesUrl = params.get('societes') || '';

  const moisActifs = useMemo(() => (moisStr ? moisStr.split(',').filter(Boolean) : []), [moisStr]);

  const couleurDe = useMemo(() => {
    const m = {}; societesDispo.forEach((s, i) => { m[s.code] = COULEURS[i % COULEURS.length]; }); return m;
  }, [societesDispo]);

  const selected = useMemo(() => {
    if (societesUrl === '__aucune__') return [];
    if (!societesUrl) return societesDispo.map((s) => s.code);
    return societesUrl.split(',').filter(Boolean);
  }, [societesUrl, societesDispo]);

  const setFiltre = (cles) => {
    const next = new URLSearchParams(params);
    Object.entries(cles).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
    setParams(next, { replace: true });
  };
  const toggleSociete = (code) => {
    const set = new Set(selected);
    set.has(code) ? set.delete(code) : set.add(code);
    const l = [...set];
    setFiltre({ societes: l.length === 0 ? '__aucune__' : l.length === societesDispo.length ? '' : l.join(',') });
  };
  const toggleMois = (code) => {
    const set = new Set(moisActifs);
    set.has(code) ? set.delete(code) : set.add(code);
    setFiltre({ mois: [...set].sort().join(',') }); // vide → supprimé
  };
  const moisModeActif = moisActifs.length > 0;
  const nbActifs = (moisModeActif || periode !== 'ytd' ? 1 : 0) + (societesUrl ? 1 : 0);

  return { params, periode, moisStr, moisActifs, moisModeActif, societesUrl, selected, couleurDe, setFiltre, toggleSociete, toggleMois, nbActifs };
}

// Fabrique le panneau de filtres commun (période/mois + sociétés).
export function PanneauVentes({ ouvert, onToggle, f, societesDispo, extra }) {
  return (
    <FiltresPanel
      ouvert={ouvert} onToggle={onToggle}
      periodes={PERIODES}
      periode={f.moisModeActif ? null : f.periode}
      onPeriode={(code) => f.setFiltre({ periode: code === 'ytd' ? '' : code, mois: '' })}
      moisDispo={MOIS_DISPO} moisActifs={f.moisActifs} onToggleMois={f.toggleMois}
      societesDispo={societesDispo} societesActives={f.selected}
      onToggleSociete={f.toggleSociete}
      onToutesSocietes={() => f.setFiltre({ societes: '' })}
      onAucuneSociete={() => f.setFiltre({ societes: '__aucune__' })}
      couleurDe={f.couleurDe} nbActifs={f.nbActifs}
      onReset={() => f.setFiltre({ societes: '', periode: '', mois: '' })}
      categoriesDispo={extra?.categoriesDispo} categoriesActives={extra?.categoriesActives}
      onToggleCategorie={extra?.onToggleCategorie} titreCategories={extra?.titreCategories}
      choix={extra?.choix}
    />
  );
}

// Toggle de panneau mémorisé (même clé que les autres rapports).
// ⚠ Sous 900 px, le panneau devient un tiroir posé PAR-DESSUS la page
// (FiltresPanel.css) : ouvert d'office, il masquait un quart du rapport sur
// téléphone (audit du 2026-10-07). Il y démarre donc toujours replié, quelle
// que soit la préférence mémorisée ; le rail permet de l'ouvrir.
const ecranEtroit = () => { try { return window.matchMedia('(max-width: 900px)').matches; } catch (e) { return false; } };
export function usePanneau() {
  const [ouvert, setOuvert] = React.useState(() => {
    if (ecranEtroit()) return false;
    try { return localStorage.getItem('bi-filtres-ouverts') !== 'non'; } catch (e) { return true; }
  });
  const toggle = () => setOuvert((o) => { try { localStorage.setItem('bi-filtres-ouverts', o ? 'non' : 'oui'); } catch (e) {} return !o; });
  return [ouvert, toggle];
}
